import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import jsQR from 'jsqr';

export type ScannerStatus = 'idle' | 'starting' | 'active' | 'error';

interface UseQrScannerOptions {
  /** Called once per successfully decoded frame -- caller is responsible for de-duplication. */
  onDecode: (rawValue: string) => void;
  /** When false, the camera is not started (or is torn down if already running). */
  enabled: boolean;
}

interface UseQrScannerResult {
  videoRef: RefObject<HTMLVideoElement>;
  status: ScannerStatus;
  errorMessage: string | null;
  torchSupported: boolean;
  torchOn: boolean;
  toggleTorch: () => void;
}

// Decoding every animation frame (60/sec) burns battery for no benefit --
// exhibition staff scan all day, so this is throttled to a rate that's still
// visually instant to the user but meaningfully lighter on the CPU/GPU.
const DECODE_INTERVAL_MS = 150;

// Downscaling the capture canvas below the camera's native resolution
// significantly reduces per-frame decode cost with no real detection loss
// for a badge held at a normal scanning distance.
const DECODE_CANVAS_WIDTH = 480;

/** Minimal ambient type for the native Shape Detection API (not yet in lib.dom.d.ts). */
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}

declare global {
  interface Window {
    BarcodeDetector?: new (options: { formats: string[] }) => BarcodeDetectorLike;
  }
}

export function useQrScanner({ onDecode, enabled }: UseQrScannerOptions): UseQrScannerResult {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const barcodeDetectorRef = useRef<BarcodeDetectorLike | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastDecodeAttemptAtRef = useRef(0);

  const [status, setStatus] = useState<ScannerStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  const decodeLoop = useCallback(() => {
    const video = videoRef.current;

    if (!video || video.readyState < video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(decodeLoop);
      return;
    }

    const now = performance.now();
    if (now - lastDecodeAttemptAtRef.current < DECODE_INTERVAL_MS) {
      rafRef.current = requestAnimationFrame(decodeLoop);
      return;
    }
    lastDecodeAttemptAtRef.current = now;

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const canvas = canvasRef.current;
    const scale = DECODE_CANVAS_WIDTH / video.videoWidth;
    canvas.width = DECODE_CANVAS_WIDTH;
    canvas.height = Math.round(video.videoHeight * scale);

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      rafRef.current = requestAnimationFrame(decodeLoop);
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    void decodeFrame(canvas, ctx, barcodeDetectorRef.current).then((value) => {
      if (value) {
        onDecode(value);
      }
      rafRef.current = requestAnimationFrame(decodeLoop);
    });
  }, [onDecode]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    async function start() {
      setStatus('starting');
      setErrorMessage(null);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        });

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        const [track] = stream.getVideoTracks();
        if (track) {
          applyContinuousAutofocus(track);

          const capabilities = track.getCapabilities?.() as MediaTrackCapabilities & { torch?: boolean };
          setTorchSupported(!!capabilities?.torch);
        }

        if (window.BarcodeDetector) {
          try {
            barcodeDetectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
          } catch {
            barcodeDetectorRef.current = null;
          }
        }

        setStatus('active');
        rafRef.current = requestAnimationFrame(decodeLoop);
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setErrorMessage(describeCameraError(err));
      }
    }

    void start();

    return () => {
      cancelled = true;

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      barcodeDetectorRef.current = null;

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      setStatus('idle');
      setTorchOn(false);
    };
  }, [enabled, decodeLoop]);

  const toggleTorch = useCallback(() => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track || !torchSupported) return;

    const next = !torchOn;
    track
      .applyConstraints({ advanced: [{ torch: next } as unknown as MediaTrackConstraintSet] })
      .then(() => setTorchOn(next))
      .catch(() => {
        // Torch control can fail transiently (e.g. thermal throttling on
        // some Android devices) -- not worth surfacing as a scan-blocking error.
      });
  }, [torchOn, torchSupported]);

  return { videoRef, status, errorMessage, torchSupported, torchOn, toggleTorch };
}

async function decodeFrame(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  detector: BarcodeDetectorLike | null
): Promise<string | null> {
  if (detector) {
    try {
      const results = await detector.detect(canvas);
      if (results.length > 0 && results[0]) {
        return results[0].rawValue;
      }
      return null;
    } catch {
      // Fall through to the jsQR fallback if the native detector errors
      // transiently (has been observed on some Android WebView builds).
    }
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const result = jsQR(imageData.data, imageData.width, imageData.height, {
    inversionAttempts: 'dontInvert' // badges are printed light-on-dark or dark-on-light consistently; skipping inversion attempts roughly doubles decode throughput
  });

  return result?.data ?? null;
}

function applyContinuousAutofocus(track: MediaStreamTrack): void {
  const capabilities = track.getCapabilities?.() as (MediaTrackCapabilities & { focusMode?: string[] }) | undefined;

  if (capabilities?.focusMode?.includes('continuous')) {
    track
      .applyConstraints({ advanced: [{ focusMode: 'continuous' } as unknown as MediaTrackConstraintSet] })
      .catch(() => {
        // Non-fatal: the camera falls back to its default (often
        // single-shot or fixed) focus behaviour.
      });
  }
}

function describeCameraError(err: unknown): string {
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
        return 'Camera access was denied. Enable camera permission in your browser settings, or use manual entry below.';
      case 'NotFoundError':
        return 'No camera was found on this device. Use manual entry below.';
      case 'NotReadableError':
        return 'The camera is already in use by another app. Close it and try again.';
      default:
        return 'Could not access the camera. Use manual entry below.';
    }
  }
  return 'Could not access the camera. Use manual entry below.';
}
